import { Folder, FolderEntityType } from '@/components/AiChat/ai-chat/types';

export interface CreateFolderRequest {
  name: string;
  entity_type: FolderEntityType;
  topic_id?: string;
  workspace_id?: string;
  parent_id?: string | null;
}

export interface UpdateFolderRequest {
  name?: string;
  parent_id?: string | null;
}

// Fetch folders by topic ID (for topic-level folders)
const fetchFolders = async (topicId: string, entityType?: FolderEntityType): Promise<Folder[]> => {
  if (!topicId || topicId === 'undefined') {
    console.warn('Cannot fetch folders: invalid topicId', topicId);
    return [];
  }

  const params = new URLSearchParams({ topic_id: topicId });
  if (entityType) {
    params.append('entity_type', entityType);
  }

  const response = await fetch(`/api/folders?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch folders');
  }

  return response.json();
};

// Fetch folders by workspace ID (for workspace-level folders)
const fetchWorkspaceFolders = async (workspaceId: string, entityType?: FolderEntityType): Promise<Folder[]> => {
  if (!workspaceId || workspaceId === 'undefined') {
    console.warn('Cannot fetch workspace folders: invalid workspaceId', workspaceId);
    return [];
  }

  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (entityType) {
    params.append('entity_type', entityType);
  }

  const response = await fetch(`/api/folders?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch workspace folders');
  }

  return response.json();
};

const createFolder = async (data: CreateFolderRequest): Promise<Folder> => {
  const response = await fetch('/api/folders', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create folder');
  }

  return response.json();
};

const updateFolder = async (id: string, data: UpdateFolderRequest): Promise<Folder> => {
  const response = await fetch(`/api/folders/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update folder');
  }

  return response.json();
};

const deleteFolder = async (id: string): Promise<void> => {
  const response = await fetch(`/api/folders/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete folder');
  }
};

const moveItemToFolder = async (
  entityType: 'datasource' | 'tamed_data' | 'artifact',
  itemId: string,
  folderId: string | null
): Promise<void> => {
  let endpoint: string;
  if (entityType === 'datasource') {
    endpoint = `/api/datasources/${itemId}/folder`;
  } else if (entityType === 'artifact') {
    endpoint = `/api/artifacts/${itemId}`;
  } else {
    endpoint = `/api/tamed-data/${itemId}/folder`;
  }

  // Artifacts use fk_folder_id, others use folder_id
  const bodyKey = entityType === 'artifact' ? 'fk_folder_id' : 'folder_id';

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ [bodyKey]: folderId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to move item to folder');
  }
};

const moveItemsToFolderBatch = async (
  entityType: 'datasource' | 'tamed_data' | 'artifact',
  itemIds: string[],
  folderId: string | null
): Promise<void> => {
  let endpoint: string;
  if (entityType === 'datasource') {
    endpoint = '/api/datasources/batch/folder';
  } else if (entityType === 'artifact') {
    endpoint = '/api/artifacts/batch';
  } else {
    endpoint = '/api/tamed-data/batch/folder';
  }

  // Artifacts batch endpoint uses artifactIds and fk_folder_id
  const body = entityType === 'artifact'
    ? { artifactIds: itemIds, fk_folder_id: folderId }
    : { ids: itemIds, folder_id: folderId };

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to move items to folder');
  }
};

export const foldersService = {
  fetchFolders,
  fetchWorkspaceFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  moveItemToFolder,
  moveItemsToFolderBatch,
};
